import React, { useState, useMemo } from "react";
import { Search, ChevronDown, X, Check } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";

/**
 * Searchable single-select for Masters Configuration.
 * Menu is portaled so it is not clipped by ContentCard overflow.
 */
export function SearchableSelect({
  value,
  onValueChange,
  options = [],
  placeholder = "Select a field",
  searchPlaceholder = "Search...",
  disabled = false,
  loading = false,
  className,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase().trim();
    return options.filter((opt) => String(opt).toLowerCase().includes(q));
  }, [options, search]);

  const handleOpenChange = (open) => {
    if (disabled || loading) return;
    setIsOpen(open);
    if (open) setSearch("");
  };

  const handleSelect = (opt) => {
    onValueChange?.(opt);
    setIsOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onValueChange?.("");
    setIsOpen(false);
    setSearch("");
  };

  const triggerLabel = loading ? "Loading..." : value || placeholder;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled || loading}
          className={cn(
            "flex h-11 w-full items-center justify-between rounded-xl border border-[var(--color-text-border)] bg-white px-3 py-2 text-sm text-left shadow-sm transition",
            "focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <span className="flex items-center gap-1 shrink-0 ml-2">
            {value && !disabled && !loading && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear selection"
                onClick={handleClear}
                onPointerDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClear(e);
                  }
                }}
                className="p-0.5 rounded hover:bg-[var(--color-workspace)] text-[var(--color-text-secondary)]"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl border border-[var(--color-text-border)] bg-white shadow-xl overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-2 border-b border-[var(--color-text-border)]/60 flex items-center gap-2 bg-[var(--color-workspace)]/30">
          <Search className="h-4 w-4 text-[var(--color-text-secondary)] shrink-0" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent border-none outline-none text-xs w-full h-7 py-1"
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-2.5 text-xs text-[var(--color-text-secondary)]">
              No matching fields
            </div>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => handleSelect(opt)}
                className={cn(
                  "w-full text-left px-3 py-2 rounded-lg text-sm transition cursor-pointer flex items-center justify-between gap-2",
                  opt === value
                    ? "bg-[var(--color-workspace)] text-[var(--color-primary)] font-semibold"
                    : "text-foreground hover:bg-[var(--color-workspace)]/50"
                )}
              >
                <span className="truncate">{opt}</span>
                {opt === value && (
                  <Check className="h-3.5 w-3.5 text-[var(--color-primary)] shrink-0" />
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
